import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

const VOICE_SAMPLES_FR = [
  "pneus Toyota 4x4",
  "moteur Peugeot 206",
  "freins avant BMW",
  "phare Audi A4",
  "pare-chocs Renault",
  "batterie voiture",
  "amortisseurs neufs",
  "volant Mercedes",
  "sièges en cuir",
  "klaxon camion"
];

const VOICE_SAMPLES_EN = [
  "Toyota 4x4 tires",
  "Peugeot 206 engine",
  "BMW front brakes",
  "Audi A4 headlight",
  "Renault bumper",
  "car battery",
  "new shock absorbers",
  "Mercedes steering wheel",
  "leather seats",
  "truck horn"
];

const HeroSearchSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedText, setRecordedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [userCountry, setUserCountry] = useState('Cameroun');
  const [locationLoading, setLocationLoading] = useState(false);
  const [pulseScale, setPulseScale] = useState(1);
  
  const recordingTimerRef = useRef(null);
  const audioLevelIntervalRef = useRef(null);
  const pulseIntervalRef = useRef(null);

  const getVoiceSamples = () => {
    return i18n.language === 'fr' ? VOICE_SAMPLES_FR : VOICE_SAMPLES_EN;
  };

  useEffect(() => {
    fetchCategories();
    detectUserCountry();
    
    return () => {
      clearAllIntervals();
    };
  }, []);

  const clearAllIntervals = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (audioLevelIntervalRef.current) clearInterval(audioLevelIntervalRef.current);
    if (pulseIntervalRef.current) clearInterval(pulseIntervalRef.current);
  };

  const detectUserCountry = async () => {
    try {
      setLocationLoading(true);
      
      const savedCountry = localStorage.getItem('userCountry');
      if (savedCountry) {
        setUserCountry(savedCountry);
        setLocationLoading(false);
        return;
      }

      // Détection par IP sur le web
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        let country = data.country_name || 'Cameroun';
        
        country = formatCountryName(country);
        
        localStorage.setItem('userCountry', country);
        setUserCountry(country);
      } catch (error) {
        console.error('IP detection error:', error);
        setUserCountry('Cameroun');
        localStorage.setItem('userCountry', 'Cameroun');
      } finally {
        setLocationLoading(false);
      }
    } catch (error) {
      console.error('Country detection error:', error);
      setUserCountry('Cameroun');
      setLocationLoading(false);
    }
  };

  const formatCountryName = (countryName) => {
    const countryMap = {
      'Cameroon': 'Cameroun',
      'Ivory Coast': 'Côte d\'Ivoire',
      'Cote d\'Ivoire': 'Côte d\'Ivoire',
      'Senegal': 'Sénégal',
      'Gabon': 'Gabon',
      'Mali': 'Mali',
      'Burkina Faso': 'Burkina Faso',
      'Benin': 'Bénin',
      'Togo': 'Togo',
      'Niger': 'Niger',
      'Democratic Republic of the Congo': 'République Démocratique du Congo',
      'Congo': 'République du Congo',
      'Republic of the Congo': 'République du Congo',
      'Guinea': 'Guinée',
      'Madagascar': 'Madagascar',
      'Tunisia': 'Tunisie',
      'Morocco': 'Maroc',
      'Algeria': 'Algérie',
      'Mauritania': 'Mauritanie'
    };
    
    return countryMap[countryName] || countryName;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.autocasses.com/api/categories/all');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.message);
      setCategories(getDefaultCategories());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultCategories = () => {
    return [
      { id: 1, nom: 'Auto', description: '', photo_url: '' },
      { id: 2, nom: 'Moto', description: '', photo_url: '' },
      { id: 3, nom: 'Moteurs', description: '', photo_url: '' },
      { id: 4, nom: 'Carrosserie', description: '', photo_url: '' },
      { id: 5, nom: 'Accessoires', description: '', photo_url: '' },
      { id: 6, nom: 'Outils', description: '', photo_url: '' },
      { id: 7, nom: 'Véhicules Complets', description: '', photo_url: '' }
    ];
  };

  const mapApiCategoryToFilter = (category) => {
    const iconMap = {
      'moto': 'Bike',
      'Moto': 'Bike',
      'Moteur': 'Cog',
      'Moteurs': 'Cog',
      'vehicule': 'Car',
      'voiture': 'Car',
      'Auto': 'Car',
      'art': 'Palette',
      'lubrifiant': 'Droplet',
      'huile': 'Droplet',
      'outils': 'Wrench',
      'accessoires': 'Package',
      'carrosserie': 'Shield'
    };

    const nomLower = category.nom.toLowerCase();
    let iconName = 'Package';

    for (const [key, value] of Object.entries(iconMap)) {
      if (nomLower.includes(key.toLowerCase())) {
        iconName = value;
        break;
      }
    }

    return {
      key: category.id.toString(),
      label: category.nom,
      icon: iconName,
      count: '0',
      originalCategory: category
    };
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/rechercher', { 
        state: { 
          query: searchQuery,
          category: null,
          categoryName: null
        }
      });
    }
  };

  const handleVoiceSearch = () => {
    setShowVoiceModal(true);
    setRecordedText('');
    setRecordingTime(0);
    setAudioLevel(0);
    setIsRecording(false);
    setIsProcessing(false);
  };

  const startRecording = () => {
    setIsRecording(true);
    setRecordedText('');
    setRecordingTime(0);
    setAudioLevel(0);
    
    startPulseAnimation();
    
    audioLevelIntervalRef.current = setInterval(() => {
      setAudioLevel(prev => {
        const variation = Math.random() * 20 - 10;
        const newLevel = Math.max(0, Math.min(100, prev + variation));
        return newLevel;
      });
    }, 100);
    
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    
    const stopTime = 5000 + Math.random() * 3000;
    setTimeout(() => {
      stopRecording();
    }, stopTime);
  };

  const stopRecording = () => {
    setIsRecording(false);
    setIsProcessing(true);
    
    clearAllIntervals();
    stopPulseAnimation();
    setAudioLevel(0);
    
    setTimeout(() => {
      const voiceSamples = getVoiceSamples();
      const randomIndex = Math.floor(Math.random() * voiceSamples.length);
      const simulatedText = voiceSamples[randomIndex];
      
      setRecordedText(simulatedText);
      setIsProcessing(false);
      
      setSearchQuery(simulatedText);
    }, 1500);
  };

  const startPulseAnimation = () => {
    let direction = 1;
    let currentScale = 1;
    
    pulseIntervalRef.current = setInterval(() => {
      if (currentScale >= 1.3) direction = -1;
      if (currentScale <= 1) direction = 1;
      
      currentScale += direction * 0.05;
      setPulseScale(currentScale);
    }, 50);
  };

  const stopPulseAnimation = () => {
    if (pulseIntervalRef.current) {
      clearInterval(pulseIntervalRef.current);
    }
    setPulseScale(1);
  };

  const handleFilterClick = (filter) => {
    navigate('/rechercher', { 
      state: { 
        query: null,
        category: filter.key,
        categoryName: filter.label
      }
    });
  };

  const handleAdvancedSearch = () => {
    navigate('/rechercher', { 
      state: { 
        query: searchQuery,
        category: null,
        categoryName: null
      }
    });
  };

  const handleUseVoiceText = () => {
    if (recordedText.trim()) {
      setSearchQuery(recordedText);
      setShowVoiceModal(false);
    }
  };

  const handleSearchFromVoice = () => {
    if (recordedText.trim()) {
      setShowVoiceModal(false);
      navigate('/search', { 
        state: { 
          query: recordedText,
          category: null,
          categoryName: null
        }
      });
    } else {
      alert(t('Veuillez d\'abord enregistrer une recherche vocale'));
    }
  };

  const quickFilters = categories.map(mapApiCategoryToFilter);

  return (
    <>
      <Helmet>
        <title>{t('AutoCasse - Trouvez vos pièces auto')}</title>
      </Helmet>

      <div className="bg-blue-50 py-3 px-4 md:px-16">
        {/* Quick Filter Chips */}
        {loading ? (
          <div className="py-5 text-center">
            <p className="text-slate-500 text-sm">
              {t('Chargement des catégories...')}
            </p>
          </div>
        ) : error ? (
          <div className="py-5 text-center">
            <p className="text-red-500 text-sm mb-2">
              {t('Erreur de chargement')}
            </p>
            <button 
              onClick={fetchCategories}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
            >
              {t('Réessayer')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto pb-2 -mx-4 md:-mx-16 px-4 md:px-16">
            <div className="flex space-x-2 min-w-max">
              {quickFilters.map((filter) => (
                <button
                  key={filter.key}
                  className="flex items-center bg-white border border-slate-200 rounded-full px-3 py-2 space-x-2 shadow-sm hover:shadow-md transition-shadow whitespace-nowrap"
                  onClick={() => handleFilterClick(filter)}
                >
                  <Icon name={filter.icon} size={16} color="#3b82f6" />
                  <span className="text-slate-800 text-xs font-medium">
                    {filter.label}
                  </span>
                  {filter.count !== '0' && (
                    <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full">
                      {filter.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hero Content */}
        <div className="py-6 text-center max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3 leading-tight">
            {locationLoading 
              ? t('Trouvez vos pièces auto') 
              : `${t('Trouvez vos pièces auto au')} ${userCountry}`
            }
          </h1>
          
          {locationLoading && (
            <div className="flex items-center justify-center mb-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <p className="text-slate-500 text-sm ml-2">
                {t('Détection de votre pays...')}
              </p>
            </div>
          )}

          <p className="text-slate-600 text-base mb-8 leading-relaxed">
            {t('Marketplace de pièces automobiles neuves et d\'occasion, véhicules complets et services auto')}
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="w-full max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Search" size={20} color="#94a3b8" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder={t('Rechercher pièces, marques, modèles...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name="X" size={20} color="#94a3b8" className="hover:text-slate-600" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Icon name="Search" size={18} color="white" />
                {t('Rechercher')}
              </button>
            </div>
          </form>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleVoiceSearch}
              className="flex items-center justify-center bg-white/80 border border-slate-200 hover:border-blue-300 rounded-full px-6 py-2 space-x-2 transition-all duration-200 hover:shadow-sm"
            >
              <Icon name="Mic" size={16} color="#3b82f6" />
              <span className="text-blue-500 text-sm font-medium">
                {t('Recherche vocale')}
              </span>
            </button>
            
            <button
              onClick={handleAdvancedSearch}
              className="flex items-center justify-center bg-white/80 border border-slate-200 hover:border-slate-300 rounded-full px-6 py-2 space-x-2 transition-all duration-200 hover:shadow-sm"
            >
              <Icon name="Sliders" size={16} color="#64748b" />
              <span className="text-slate-600 text-sm font-medium">
                {t('Recherche avancée')}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de recherche vocale simulée */}
      {showVoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">
                {t('Recherche vocale')}
              </h2>
              <button
                onClick={() => {
                  setShowVoiceModal(false);
                  clearAllIntervals();
                  setIsRecording(false);
                  setIsProcessing(false);
                }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <Icon name="X" size={24} color="#475569" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex flex-col items-center mb-8">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-lg transition-transform duration-200"
                  style={{
                    transform: `scale(${pulseScale})`,
                    backgroundColor: isRecording ? '#ef4444' : '#3b82f6'
                  }}
                >
                  {isRecording ? (
                    <Icon name="Mic" size={48} color="white" />
                  ) : (
                    <Icon name="MicOff" size={48} color="white" />
                  )}
                </div>
                
                {isRecording && (
                  <div className="flex items-end h-16 gap-2 mb-4">
                    {[0, 1, 2, 3, 4].map((index) => (
                      <div
                        key={index}
                        className="w-3 bg-emerald-400 rounded transition-all duration-100"
                        style={{
                          height: `${audioLevel * 0.8 + 10}px`,
                          backgroundColor: audioLevel > index * 20 ? '#10b981' : '#e2e8f0'
                        }}
                      />
                    ))}
                  </div>
                )}
                
                {isRecording && (
                  <div className="text-2xl font-semibold text-slate-800 mb-4">
                    {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                    {(recordingTime % 60).toString().padStart(2, '0')}
                  </div>
                )}
              </div>

              <p className="text-center text-slate-600 mb-6">
                {isRecording 
                  ? t('Simulation d\'enregistrement...')
                  : isProcessing
                  ? t('Simulation de traitement en cours...')
                  : t('Appuyez sur le bouton pour simuler la reconnaissance vocale')
                }
              </p>

              {recordedText ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                  <p className="text-sm font-semibold text-slate-500 mb-2">
                    {t('Texte simulé:')}
                  </p>
                  <p className="text-lg font-medium text-slate-800">
                    {recordedText}
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <p className="text-sm font-semibold text-blue-700 mb-2">
                    {t('Exemples simulés:')}
                  </p>
                  <p className="text-blue-800 italic">
                    {i18n.language === 'fr'
                      ? '"pneus Toyota", "moteur Peugeot 206", "freins avant"'
                      : '"Toyota tires", "Peugeot 206 engine", "front brakes"'
                    }
                  </p>
                </div>
              )}

              <div className="flex items-start bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <Icon name="Info" size={20} color="#f59e0b" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800 ml-2">
                  {t('Ceci est une simulation. Le texte est généré aléatoirement.')}
                </p>
              </div>

              <div className="space-y-4">
                {!isRecording && !isProcessing ? (
                  <button
                    onClick={startRecording}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center space-x-3 transition-colors"
                  >
                    <Icon name="Mic" size={20} color="white" />
                    <span>{t('Démarrer la simulation')}</span>
                  </button>
                ) : isRecording ? (
                  <button
                    onClick={stopRecording}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center space-x-3 transition-colors"
                  >
                    <Icon name="Square" size={20} color="white" />
                    <span>{t('Arrêter')}</span>
                  </button>
                ) : (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                    <p className="text-slate-600">
                      {t('Simulation de traitement...')}
                    </p>
                  </div>
                )}
              </div>

              {recordedText && !isRecording && !isProcessing && (
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button
                    onClick={handleUseVoiceText}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <Icon name="Text" size={18} color="#64748b" />
                    {t('Utiliser le texte')}
                  </button>
                  <button
                    onClick={handleSearchFromVoice}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Icon name="Search" size={20} color="white" />
                    <span>{t('Rechercher')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .overflow-x-auto {
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        
        .overflow-x-auto::-webkit-scrollbar {
          height: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }
      `}</style>
    </>
  );
};

export default HeroSearchSection;